import { useEffect, useState, Fragment, useMemo } from "react";
import { Row, Col, Card, Form, ListGroup, Button } from "react-bootstrap";
import AppTab from "../../components/AppTab/AppTab";
import { PERMISSION_MAP as APP_PERMISSION_MAP } from "../../helpers/PortalPermission";
import { PERMISSION_MAP as REPORT_PERMISSION_MAP } from "../../helpers/ReportPermission";
import DefaultConfig from "../../config/default";
import FolderIcon from "react-ionicons/lib/Folder";
import { ArrowForward, ArrowBack } from "react-ionicons";
import PermissionManager, {
  default as PM,
} from "../../helpers/PermissionManager";
import "./InviteUserPermissions.scss";

const ReportPermissionsTab = ({
  selectedVAccount,
  reportPermissions,
  setReportPermissions,
  userAccessType,
}) => {
  const repPermission = REPORT_PERMISSION_MAP[DefaultConfig.DOMAIN_NAME] || [];
  const targetPerms = reportPermissions[selectedVAccount.entityId];

  const onChange = (event) => {
    let { name, value } = event.target;

    targetPerms[name] = value === "true" ? true : false;
    setReportPermissions({ ...reportPermissions });
  };

  return (
    <>
      {repPermission.map((item, idx) => {
        if (item.isUsable && !item.isUsable(selectedVAccount)) {
          return <Fragment key={idx}></Fragment>;
        }
        return (
          <Row key={idx} className="permission-field g-0 pt-2 pb-2">
            <Col className="p-2 fw-bold" lg={4}>
              {item.label}
            </Col>
            <Col lg={4}>
              <Form.Select
                disabled={userAccessType === "business_owner"}
                onChange={onChange}
                name={item.key}
                value={targetPerms[item.key]}
              >
                {item.choice.map((v, idx) => {
                  return (
                    <option key={idx} value={v.value}>
                      {v.label}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>
          </Row>
        );
      })}
    </>
  );
};

const PortalPermissionsTab = ({
  selectedVAccount,
  portalPermissions,
  setPortalPermissions,
  userAccessType,
}) => {
  const appPermission = APP_PERMISSION_MAP[DefaultConfig.DOMAIN_NAME] || [];
  const targetPerms = portalPermissions[selectedVAccount.entityId];

  const onChange = (event) => {
    let { name, value } = event.target;
    const newValue = value === "true" ? true : false;
    targetPerms[name] = newValue;
    setPortalPermissions({ ...portalPermissions }, newValue);
  };
  return (
    <>
      {appPermission.map((item, idx) => {
        // todo - remove when drive available for FFDA.
        if (
          DefaultConfig.DOMAIN_NAME === "FIDUCIAL_FEDERATED" &&
          item.key.includes("drive")
        )
          return <Fragment key={idx}></Fragment>;
        if (item.isUsable && !item.isUsable(selectedVAccount)) {
          return <Fragment key={idx}></Fragment>;
        }
        return (
          <Row key={idx} className="permission-field g-0 pt-2 pb-2">
            <Col className="p-2 fw-bold" lg={4}>
              {item.label}
            </Col>
            <Col lg={4}>
              <Form.Select
                disabled={userAccessType === "business_owner"}
                onChange={onChange}
                name={item.key}
                value={targetPerms[item.key]}
              >
                {item.choice.map((v, idx) => {
                  return (
                    <option key={idx} value={v.value}>
                      {v.label}
                    </option>
                  );
                })}
              </Form.Select>
            </Col>
            <Col lg={4}>
              {item.goToTabOption && (
                <>
                  <Button
                    variant="link"
                    disabled={!targetPerms[item.key]}
                    onClick={() =>
                      document
                        .getElementById(item.goToTabOption.tabName)
                        .click()
                    }
                  >
                    {item.goToTabOption.label}
                  </Button>
                </>
              )}
            </Col>
          </Row>
        );
      })}
    </>
  );
};

const DrivePermissionsTab = ({
  selectedVAccount,
  drivePermissions,
  setDrivePermissions,
  userAccessType,
}) => {
  const DFLT_ICO_PROPS = {
    height: "32px",
    width: "32px",
    color: "inherit",
    className: "type-ico",
  };
  const targetDrivePerms = drivePermissions[selectedVAccount.entityId];

  const onChange = (event) => {
    const { name, value } = event.target;

    for (let perm of targetDrivePerms) {
      if (name === perm.code) {
        perm.securityLevel = Number(value);
      }
    }

    drivePermissions[selectedVAccount.entityId] = targetDrivePerms;
    setDrivePermissions({ ...drivePermissions });
  };

  useEffect(() => {}, [selectedVAccount]);

  return (
    <>
      {(targetDrivePerms || []).map((item, idx) => (
        <Row key={idx} className="permission-field g-0 pt-2 pb-2">
          <Col className="fw-bold p-2" lg={10} md={12}>
            <span
              className="me-2"
              style={{
                color: "var(--main-brand-color)",
                fill: "var(--main-brand-color)",
              }}
            >
              <FolderIcon {...DFLT_ICO_PROPS} />
            </span>
            {item.label}
          </Col>
          <Col lg={2} md={4}>
            <Form.Select
              disabled={userAccessType === "business_owner"}
              onChange={onChange}
              value={item.securityLevel}
              name={item.code}
            >
              <option value={0}>No</option>
              <option value={7}>Yes</option>
            </Form.Select>
          </Col>
        </Row>
      ))}
    </>
  );
};

const PermissionsCard = ({
  selectedVAccount,
  portalPermissions,
  drivePermissions,
  reportPermissions,
  setPortalPermissions,
  setDrivePermissions,
  setReportPermissions,
  selectVAccount,
  userAccessType,
  profile,
  session,
}) => {
  return (
    <Card className="shadow-sm">
      <Card.Body className="p-0">
        <div className="d-flex p-3 permission-card-header">
          <div className="flex-grow-1 ms-1 me-2">
            <strong>{selectedVAccount.name}</strong>
          </div>
          <ArrowBack color="#fff" onClick={() => selectVAccount(null)} />
        </div>
        {PM.isNotFederated(session, profile) && (
          <AppTab>
            <AppTab.Item label="General">
              <div className="p-2">
                <PortalPermissionsTab
                  selectedVAccount={selectedVAccount}
                  portalPermissions={portalPermissions}
                  setPortalPermissions={(newPortalPermissions, newValue) => {
                    setPortalPermissions(
                      selectedVAccount,
                      newPortalPermissions,
                      newValue
                    );
                  }}
                  userAccessType={userAccessType}
                />
              </div>
            </AppTab.Item>
            <AppTab.Item label="Documents">
              <div className="p-2">
                <DrivePermissionsTab
                  selectedVAccount={selectedVAccount}
                  drivePermissions={drivePermissions}
                  setDrivePermissions={setDrivePermissions}
                  userAccessType={userAccessType}
                />
              </div>
            </AppTab.Item>
          </AppTab>
        )}
        {PM.isFederated(session, profile) && (
          <AppTab>
            <AppTab.Item label="General">
              <div className="p-2">
                <PortalPermissionsTab
                  selectedVAccount={selectedVAccount}
                  portalPermissions={portalPermissions}
                  setPortalPermissions={(newPortalPermissions, newValue) => {
                    setPortalPermissions(
                      selectedVAccount,
                      newPortalPermissions,
                      newValue
                    );
                  }}
                  userAccessType={userAccessType}
                />
              </div>
            </AppTab.Item>
            <AppTab.Item label="Documents">
              <div className="p-2">
                <DrivePermissionsTab
                  selectedVAccount={selectedVAccount}
                  drivePermissions={drivePermissions}
                  setDrivePermissions={setDrivePermissions}
                  userAccessType={userAccessType}
                />
              </div>
            </AppTab.Item>
            <AppTab.Item label="Reports">
              <div className="p-2">
                <ReportPermissionsTab
                  selectedVAccount={selectedVAccount}
                  reportPermissions={reportPermissions}
                  setReportPermissions={setReportPermissions}
                  userAccessType={userAccessType}
                />
              </div>
            </AppTab.Item>
          </AppTab>
        )}
      </Card.Body>
    </Card>
  );
};

const UpdatePortalPermissionsBasedOnLogin = (
  portalPermissions,
  virtualAccount,
  mainEntityId,
  value
) => {
  if (virtualAccount.entityId !== mainEntityId && value === true) {
    portalPermissions[mainEntityId]["login"] = value;
  } else if (virtualAccount.entityId === mainEntityId && value === false) {
    for (const entityId in portalPermissions) {
      if (entityId !== mainEntityId) {
        portalPermissions[entityId]["login"] = false;
      }
    }
  }
  return portalPermissions;
};

const VAccountListItem = ({
  virtualAccount,
  portalPermissions,
  targetPortalPermissions,
  setPortalPermissions,
  userAccessType,
  selectVAccount,
  profile,
  session,
}) => {
  const mainEntityId = useMemo(
    () => virtualAccount.parentEntityId || virtualAccount.entityId,
    [virtualAccount]
  );
  const checkAccess = () => {
    if (targetPortalPermissions && targetPortalPermissions["login"])
      return targetPortalPermissions["login"];
    return false;
  };

  const onSwitchChange = (event) => {
    const valueString = event.target.value;
    const value = valueString === "true" ? true : false;
    portalPermissions[virtualAccount.entityId]["login"] = value;
    let newPortalPermissions = UpdatePortalPermissionsBasedOnLogin(
      { ...portalPermissions },
      virtualAccount,
      mainEntityId,
      value
    );
    setAccess(value);
    setPortalPermissions({ ...newPortalPermissions });
  };

  const [access, setAccess] = useState(checkAccess());

  useEffect(() => {
    setAccess(checkAccess());
  }, [userAccessType, portalPermissions]);

  return (
    <ListGroup.Item className="mb-2 p-3 permission-list-element">
      <div className="d-flex">
        <div className="ms-1 me-2">
          <Form.Check
            type="switch"
            id={virtualAccount.entityId}
            checked={access}
            value={!access}
            onChange={onSwitchChange}
            disabled={userAccessType === "business_owner"}
          />
        </div>
        <div className="ms-1 me-2 flex-grow-1">{virtualAccount.name}</div>
        {/* <ArrowForward 
                    onClick={() => selectVAccount(virtualAccount.entityId)}
                /> */}
        <Button
          className="btn-sm"
          onClick={() => selectVAccount(virtualAccount.entityId)}
        >
          Set permissions
        </Button>
      </div>
    </ListGroup.Item>
  );
};

const VAccountList = ({
  vAccounts,
  portalPermissions,
  setPortalPermissions,
  selectVAccount,
  userAccessType,
  profile,
  session,
}) => {
  const vAccountList = Object.values(vAccounts);

  // useEffect(() => {
  // }, [userAccessType]);

  return (
    <ListGroup className="p-0">
      {vAccountList.map((item) => (
        <VAccountListItem
          key={item.entityId}
          virtualAccount={item}
          portalPermissions={portalPermissions}
          setPortalPermissions={setPortalPermissions}
          targetPortalPermissions={portalPermissions[item.entityId]}
          userAccessType={userAccessType}
          selectVAccount={selectVAccount}
          profile={profile}
          session={session}
        />
      ))}
    </ListGroup>
  );
};

const InviteUserPermissions = ({
  vAccounts,
  portalPermissions,
  drivePermissions,
  reportPermissions,
  setPortalPermissions,
  setDrivePermissions,
  setReportPermissions,
  userAccessType,
  profile,
  session,
}) => {
  const mainEntityId = useMemo(() => {
    let mainEntityId = null;
    for (const entityId in vAccounts) {
      if (vAccounts[entityId].parentEntityId === null) {
        mainEntityId = entityId;
        break;
      }
    }
    return mainEntityId;
  }, [vAccounts]);
  const [selectedVAccount, setSelectedVAccount] = useState(null);
  const selectVAccount = (vAccountId) => {
    setSelectedVAccount(vAccounts[vAccountId]);
  };

  const handleOnChangePortalPermission = (
    selectedVAccount,
    changePortalPermission,
    newValue
  ) => {
    let newPortalPermissions = UpdatePortalPermissionsBasedOnLogin(
      { ...changePortalPermission },
      selectedVAccount,
      mainEntityId,
      newValue
    );
    setPortalPermissions(newPortalPermissions);
  };

  return (
    <>
      {!selectedVAccount ? (
        <VAccountList
          vAccounts={vAccounts}
          portalPermissions={portalPermissions}
          setPortalPermissions={setPortalPermissions}
          selectVAccount={selectVAccount}
          userAccessType={userAccessType}
          profile={profile}
          session={session}
        />
      ) : (
        <PermissionsCard
          selectedVAccount={selectedVAccount}
          portalPermissions={portalPermissions}
          drivePermissions={drivePermissions}
          reportPermissions={reportPermissions}
          setPortalPermissions={handleOnChangePortalPermission}
          setDrivePermissions={setDrivePermissions}
          setReportPermissions={setReportPermissions}
          selectVAccount={selectVAccount}
          userAccessType={userAccessType}
          profile={profile}
          session={session}
        />
      )}
    </>
  );
};

export default InviteUserPermissions;
